import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Check, Clock, ChefHat, Bell, PackageCheck, XCircle, MapPin, CreditCard, Wallet, FileDown, RefreshCw, WifiOff, ArrowLeft } from "lucide-react";
import { publicApi, money, fetchSite } from "./orderApi";
import { PayOnlineSheet } from "./PayOnlineSheet";
import { getAccountToken } from "./razorpay";
import { useFeedbackInvite } from "./FeedbackInvite";
import "./site.css";

const POLL_MS = 5000;
const STEPS = (type) => [
  { key: "pending", label: "Order received", icon: Clock, sub: "We’ve received your order." },
  { key: "accepted", label: "Accepted", icon: Check, sub: "The outlet confirmed your order." },
  { key: "preparing", label: "Preparing", icon: ChefHat, sub: "Your food is being prepared." },
  { key: "ready", label: "Ready", icon: Bell, sub: type === "dine_in" ? "Ready — our team will serve you." : "Ready for pickup at the counter." },
  { key: "served", label: type === "dine_in" ? "Served" : "Picked up", icon: PackageCheck, sub: "Enjoy your meal!" },
];
const errText = (e, fb) => { const d = e?.response?.data?.detail; return (d && (d.message || (typeof d === "string" ? d : null))) || fb; };
const fmtTime = (iso, tz) => { try { return new Intl.DateTimeFormat("en-IN", { hour: "numeric", minute: "2-digit", timeZone: tz || "Asia/Kolkata" }).format(new Date(iso)); } catch { return new Date(iso).toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" }); } };

const payLabel = (p) => {
  if (!p) return "—";
  if (p.method === "razorpay") {
    if (p.refund_status) return p.refund_status === "refunded" ? `Paid online · Refunded ${money((p.refunded_paise || 0) / 100)}` : `Paid online · Partial refund ${money((p.refunded_paise || 0) / 100)}`;
    return { paid: "Paid online", created: "Awaiting online payment", expired: "Payment window expired", failed: "Payment failed" }[p.status] || `Online · ${p.status}`;
  }
  return p.status === "paid" ? `Paid at outlet${p.settled_mode ? ` (${p.settled_mode.toUpperCase()})` : ""}` : "Unpaid · Pay at outlet";
};

/** Estimated-ready block: staff estimate only (no invented countdown); delay message once the estimate passes. */
const Estimate = ({ o, tz, now }) => {
  if (["ready", "served", "rejected", "cancelled", "awaiting_payment"].includes(o.status)) return null;
  if (o.status === "pending") return <div className="os-wait" data-testid="track-waiting">Waiting for restaurant confirmation{o.token_no == null ? " · your token number appears after acceptance" : ""}.</div>;
  const est = o.estimate;
  if (!est?.ready_by) return <div className="os-wait" style={{ background: "var(--soft)", borderColor: "var(--line)", color: "var(--muted)" }} data-testid="track-no-estimate">{o.status === "accepted" ? "Accepted — preparation time will be shared by the outlet." : "Being prepared — no time estimate shared yet."}</div>;
  const late = new Date(est.ready_by).getTime() < now;
  return (
    <div className="os-eta" data-testid="track-estimate">
      <div className="label">{late ? "Taking a little longer" : "Estimated ready"}</div>
      <div className="value" data-testid="track-estimate-time">{fmtTime(est.ready_by, tz)}</div>
      <p data-testid="track-estimate-note">{late ? "Our team is still working on your order. Thank you for your patience." : `Estimated by the outlet · about ${est.minutes} min from ${fmtTime(est.set_at, tz)}`}</p>
    </div>
  );
};

export default function OrderTrack() {
  const { slug, requestId } = useParams();
  const [sp] = useSearchParams();
  const t = sp.get("t") || "";
  const [data, setData] = useState(null);
  const [pay, setPay] = useState(null);
  const [site, setSite] = useState(null);
  const [err, setErr] = useState(null);
  const [sync, setSync] = useState({ at: null, failing: false, busy: false });
  const [actionErr, setActionErr] = useState("");
  const [paying, setPaying] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [now, setNow] = useState(Date.now());
  const inflight = useRef(false);
  const acct = getAccountToken(slug);
  const fb = useFeedbackInvite(slug, acct, site?.brand?.logo_url, "track");
  const offerInvite = fb.offer;
  const fbSeen = useRef({ status: "", at: 0 });

  // Served while Tracking is open → same invitation as My Orders (account-scoped detail: ownership, toggle, dismissal, rated lines all decided server-side).
  const checkInvite = useCallback(async (status) => {
    if (!acct || status !== "served") return;
    if (fbSeen.current.status === status && Date.now() - fbSeen.current.at < 15000) return;
    fbSeen.current = { status, at: Date.now() };
    try { const r = await publicApi.get(`/${slug}/account/orders/${requestId}`, { params: { token: acct, _ts: Date.now() } }); offerInvite([r.data]); }
    catch { /* not signed in as this order's customer → no automatic invitation */ }
  }, [slug, requestId, acct, offerInvite]);

  const load = useCallback(async () => {
    if (inflight.current) return;
    inflight.current = true; setSync((s) => ({ ...s, busy: true }));
    try {
      const r = await publicApi.get(`/${slug}/orders/${requestId}/track`, { params: { t, _ts: Date.now() }, headers: { "Cache-Control": "no-cache" } });
      setData(r.data); setErr(null); setSync({ at: Date.now(), failing: false, busy: false });
      checkInvite(r.data.order.status);
      if (r.data.order.status === "awaiting_payment" || r.data.order.payment?.method === "razorpay") {
        try { setPay((await publicApi.get(`/${slug}/payments/${requestId}/status`, { params: { t } })).data); } catch { /* optional */ }
      }
    } catch (e) {
      if ([400, 403, 404, 422].includes(e.response?.status)) setErr("Order not found.");
      else setSync((s) => ({ ...s, failing: true, busy: false }));
    } finally { inflight.current = false; }
  }, [slug, requestId, t, checkInvite]);

  useEffect(() => {
    let alive = true;
    load();
    fetchSite(slug).then((s) => alive && setSite(s)).catch(() => {});
    const iv = setInterval(() => { if (document.visibilityState !== "hidden") load(); }, POLL_MS);
    const tick = setInterval(() => setNow(Date.now()), 30000);
    const wake = () => { if (document.visibilityState !== "hidden") load(); };
    document.addEventListener("visibilitychange", wake); window.addEventListener("focus", wake); window.addEventListener("online", wake); window.addEventListener("pageshow", wake);
    return () => { alive = false; clearInterval(iv); clearInterval(tick); document.removeEventListener("visibilitychange", wake); window.removeEventListener("focus", wake); window.removeEventListener("online", wake); window.removeEventListener("pageshow", wake); };
  }, [load, slug]);

  const color = site?.brand?.primary_color || "#b91c1c";
  if (err && !data) return <div className="os-site" style={{ display: "grid", placeItems: "center", padding: 24 }} data-testid="track-error">{err}</div>;
  if (!data) return <div className="os-site" style={{ display: "grid", placeItems: "center" }} data-testid="track-loading">Loading…</div>;
  const o = data.order;
  const awaiting = o.status === "awaiting_payment";
  const terminal = ["rejected", "cancelled"].includes(o.status);
  const steps = STEPS(o.order_type);
  const idx = steps.findIndex((s) => s.key === o.status);
  const resume = pay?.resume;
  const stepTime = { pending: o.created_at, accepted: o.accepted_at, preparing: o.preparing_at, ready: o.ready_at, served: o.served_at };

  const switchToOutlet = async () => {
    setSwitching(true); setActionErr("");
    try { await publicApi.post(`/${slug}/payments/${requestId}/switch-to-outlet`, null, { params: { t } }); await load(); }
    catch (e) { setActionErr(errText(e, "Could not switch payment method.")); }
    finally { setSwitching(false); }
  };

  return (
    <div className="os-site" style={{ "--brand": color }} data-testid="order-track">
      <div className="os-track">
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <Link to={`/order/${slug}`} className="os-icon-btn" aria-label="Back to menu" data-testid="track-back"><ArrowLeft size={18} /></Link>
          <div style={{ flex: 1, minWidth: 0 }}><h1 style={{ fontSize: 18, fontWeight: 800 }}>Order {o.order_ref}</h1><p className="os-hint" style={{ margin: 0 }}>{data.branch?.name}</p></div>
          <Link to={`/order/${slug}/account`} className="os-link" data-testid="track-my-orders">My orders</Link>
        </div>

        <div className="os-track-card">
          {terminal ? (
            <div className="os-token" style={{ background: "#fef2f2" }} data-testid="track-terminal"><XCircle size={36} style={{ color: "#b91c1c" }} /><div className="label" style={{ color: "#b91c1c", marginTop: 6 }} data-testid="track-status">{o.status_label}</div>
              {(o.rejected_reason || o.cancel_reason) && <p className="os-hint" data-testid="track-rejected-reason">{(o.rejected_reason || o.cancel_reason).replace(/_/g, " ")}</p>}
              {o.status === "cancelled" && o.payment?.status === "expired" && <p className="os-hint" data-testid="track-expired">The payment window expired, so this order was not sent to the outlet.</p>}</div>
          ) : o.token_no != null ? (
            <div className="os-token"><div className="label">Token number</div><div className="value" data-testid="track-token">{o.token_no}</div><div className="label" style={{ marginTop: 6, color: "var(--brand)" }} data-testid="track-status">{o.status === "served" && o.order_type === "dine_in" ? "Served" : o.status_label}</div></div>
          ) : (
            <div className="os-token"><div className="label">Order reference</div><div className="ref" data-testid="track-ref">{o.order_ref}</div><div className="label" style={{ marginTop: 6 }} data-testid="track-status">{o.status_label}</div>
              {awaiting && <p className="os-hint">Complete the payment to send this order to the kitchen.</p>}</div>
          )}
          {!terminal && <Estimate o={o} tz={data.branch_tz} now={now} />}
        </div>

        {awaiting && (
          <section className="os-track-card" style={{ borderColor: "#fde68a" }} data-testid="track-payment-actions">
            <div style={{ fontWeight: 800, marginBottom: 8 }}>Finish your payment</div>
            {resume ? <button type="button" onClick={() => setPaying(true)} className="os-btn primary" data-testid="track-resume-payment"><CreditCard size={16} />Pay {money(resume.amount_paise / 100)} online</button>
              : <p className="os-hint" data-testid="track-resume-unavailable">{pay?.payment_record?.last_failure ? `Last attempt: ${pay.payment_record.last_failure}` : "Checking payment status…"}</p>}
            {site?.branches?.find((b) => b.name === o.branch_name)?.payments?.pay_at_outlet !== false && (
              <button type="button" onClick={switchToOutlet} disabled={switching} className="os-btn outline" style={{ marginTop: 8 }} data-testid="track-switch-outlet"><Wallet size={16} />{switching ? "Switching…" : "Switch to Pay at Outlet"}</button>
            )}
            {actionErr && <p className="os-err" data-testid="track-action-error">{actionErr}</p>}
          </section>
        )}

        {!terminal && !awaiting && (
          <section className="os-track-card">
            <ol className="os-timeline" data-testid="track-steps">
              {steps.map((s, i) => {
                const Icon = s.icon; const done = i < idx || (i === idx && o.status === "served"); const current = i === idx && !done;
                return (
                  <li key={s.key} className={done ? "done" : current ? "current" : ""} data-testid={`track-step-${s.key}`} data-state={done ? "done" : current ? "current" : "upcoming"}>
                    <span className="dot">{done ? <Check size={14} /> : <Icon size={14} />}</span>
                    <div><b>{s.label}</b><small>{s.sub}{(done || current) && stepTime[s.key] ? ` · ${fmtTime(stepTime[s.key], data.branch_tz)}` : ""}</small></div>
                  </li>
                );
              })}
            </ol>
          </section>
        )}

        <div className="os-sync" data-testid="track-sync">
          {sync.failing ? <><WifiOff size={12} />Connection issue — retrying…</> : <><RefreshCw size={12} className={sync.busy ? "animate-spin" : ""} />{sync.at ? `Live · updated ${new Intl.DateTimeFormat("en-IN", { hour: "numeric", minute: "2-digit", second: "2-digit", timeZone: data.branch_tz || "Asia/Kolkata" }).format(new Date(sync.at))}` : "Updating…"}</>}
          <button type="button" className="os-btn text" style={{ minHeight: 24 }} onClick={load} data-testid="track-refresh">Refresh</button>
        </div>

        <section className="os-track-card" data-testid="track-branch">
          <div className="os-meta">
            <div><span className="label">Branch</span><b>{data.branch?.name}</b></div>
            <div><span className="label">Order type</span><b data-testid="track-order-type">{o.order_type === "dine_in" ? "Dine-in" : "Takeaway"}</b></div>
            <div><span className="label">Total</span><b data-testid="track-total">{money(o.pricing.total)}</b></div>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginTop: 10, fontSize: 13 }}>
            <span className="os-hint" style={{ margin: 0 }}>Payment status</span><b data-testid="track-payment" style={{ color: o.payment?.status === "paid" ? "#047857" : "var(--brand)" }}>{payLabel(o.payment)}</b>
          </div>
          {data.branch?.address && <p className="os-hint" style={{ display: "flex", gap: 6 }}><MapPin size={14} style={{ flexShrink: 0, marginTop: 2 }} />{data.branch.address}</p>}
          {o.bill_id && <Link to={`/order/${slug}/account`} className="os-link" style={{ marginTop: 8 }} data-testid="track-bill-link"><FileDown size={13} />Bill {o.bill_no ? `#${o.bill_no}` : ""} available in My Orders{getAccountToken(slug) ? "" : " (verify mobile)"}</Link>}
        </section>

        <section className="os-track-card" data-testid="track-items">
          <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {o.items.map((l, i) => (
              <li key={i} style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "8px 0", borderBottom: "1px solid var(--line)", fontSize: 14 }}>
                <div><b>{l.qty} × {l.name}</b><div className="os-hint" style={{ margin: 0 }}>{[l.variant_name, ...(l.addons || [])].filter(Boolean).join(" · ")}{l.note ? ` · “${l.note}”` : ""}</div></div>
                <b>{money(l.line_subtotal)}</b>
              </li>
            ))}
          </ul>
          <div className="os-bill">
            <div><span>Subtotal</span><b>{money(o.pricing.subtotal)}</b></div>
            {o.pricing.discount_total > 0 && <div className="disc"><span>Discount</span><b>− {money(o.pricing.discount_total)}</b></div>}
            {o.pricing.tax_total > 0 && <div><span>Taxes</span><b>{money(o.pricing.tax_total)}</b></div>}
            <div className="total"><span>Total</span><b>{money(o.pricing.total)}</b></div>
          </div>
          {o.instructions && <p className="os-hint">Instructions: {o.instructions}</p>}
        </section>
      </div>
      {paying && resume && (
        <PayOnlineSheet slug={slug} requestId={requestId} trackingToken={t} intent={resume} brand={site?.brand} color={color}
          customer={{ name: o.customer_name, phone: localStorage.getItem("sc_online_phone") || "" }}
          onPaid={() => { setPaying(false); load(); }} onCancel={() => setPaying(false)} />
      )}
      {fb.sheet}
    </div>
  );
}
